/**
 * LeaseSign — envelope viewer + canvas signature pad. Loads
 * /api/esign/envelopes/{eid}, shows the lease body, lets the current
 * signer draw a signature and submit. Once both parties sign, exposes
 * a "Download signed PDF" link.
 */
import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { PageHeader, Card, Button, Pill, Loader, Input } from "@/components/ui/Primitives";
import AutoCorrectness from "@/components/AutoCorrectness";
import { PenTool, RotateCcw, Download, CheckCircle2, FileText, Clock, Upload } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("crc_session_token") || ""}` });

// ── Signature style options for Type-mode (renders the user's typed
// legal name into the canvas using a script/cursive web-font). All fonts
// listed here MUST also be present in the global @font-face rules loaded
// by index.html so the canvas can render them without the browser
// falling back to a system serif.
const SIG_FONTS = [
  { name: "Elegant",  family: "'Great Vibes', 'Dancing Script', cursive" },
  { name: "Modern",   family: "'Dancing Script', cursive" },
  { name: "Signed",   family: "'Homemade Apple', 'Caveat', cursive" },
  { name: "Script",   family: "'Sacramento', 'Great Vibes', cursive" },
];

export default function LeaseSign() {
  const { eid } = useParams();
  const { user } = useAuth();
  const [env, setEnv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [typedName, setTypedName] = useState(user?.name || user?.email || "");
  const [saving, setSaving] = useState(false);
  // Signature-input mode: draw with pen (default), type in cursive, or
  // upload an existing signature image. All three write into the same
  // canvas so the existing submit path (`clearPad` / `submitSig`) still
  // works without touching the backend.
  const [sigMode, setSigMode] = useState("draw");
  const [typeFont, setTypeFont] = useState("Elegant");
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  // Live-stroke streaming state
  const strokeBuffer = useRef([]);           // points I've drawn since last flush
  const lastFlushRef = useRef(0);
  const witnessCanvasRefs = useRef({});       // { tenant: <canvas>, landlord: <canvas> }

  useEffect(() => {
    let cancelled = false;
    let timer = null;
    async function fetchEnv() {
      try {
        const r = await fetch(`${API}/api/esign/envelopes/${eid}`, { headers: auth() });
        const d = await r.json();
        if (!r.ok) throw new Error(d.detail || "Load failed");
        if (!cancelled) setEnv(d);
      } catch (err) { if (!cancelled) toast.error(String(err.message)); }
      finally { if (!cancelled) setLoading(false); }
    }
    fetchEnv();
    // Poll every 8s so the witness roster updates when the other party
    // signs. Stops polling once fully signed.
    timer = setInterval(() => {
      const done = env?.signers?.every((s) => !!s.signed_at);
      if (done) return;
      fetchEnv();
    }, 8000);
    return () => { cancelled = true; if (timer) clearInterval(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eid]);

  const meSigner = env?.signers?.find((s) => s.email === (user?.email || "").toLowerCase());
  const iSigned = !!meSigner?.signed_at;
  const bothSigned = env?.signers?.every((s) => !!s.signed_at);

  function coord(e) {
    const r = canvasRef.current.getBoundingClientRect();
    const t = e.touches?.[0];
    return {
      x: (t ? t.clientX : e.clientX) - r.left,
      y: (t ? t.clientY : e.clientY) - r.top,
    };
  }
  function startDraw(e) {
    e.preventDefault();
    drawing.current = true;
    const ctx = canvasRef.current.getContext("2d");
    const p = coord(e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  }
  function moveDraw(e) {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#0f172a";
    const p = coord(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    // Feed the live-stream buffer so the other party sees the pen move
    strokeBuffer.current.push([Math.round(p.x), Math.round(p.y)]);
    const now = Date.now();
    if (now - lastFlushRef.current > 300) {
      lastFlushRef.current = now;
      flushStrokes(false);
    }
  }
  function endDraw() {
    drawing.current = false;
    flushStrokes(false);
  }
  async function flushStrokes(finished) {
    if (!meSigner || iSigned) return;
    if (strokeBuffer.current.length === 0 && !finished) return;
    try {
      await fetch(`${API}/api/esign/envelopes/${eid}/live-stroke`, {
        method: "POST",
        headers: { ...auth(), "Content-Type": "application/json" },
        body: JSON.stringify({
          role: meSigner.role,
          strokes: strokeBuffer.current,
          finished,
        }),
      });
    } catch { /* best-effort */ }
  }
  function clearPad() {
    const c = canvasRef.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    strokeBuffer.current = [];
    // Reset the streamed strokes on the server too so the witness canvas clears
    flushStrokes(false);
  }

  /**
   * renderTypedSignature — write the user's typed legal name into the
   * canvas in one of the SIG_FONTS. Same canvas, same submit path, so
   * the backend receives a normal PNG data-URL and the audit-log stays
   * uniform across Draw / Type / Upload modes.
   */
  function renderTypedSignature(text, fontName) {
    const trimmed = (text || "").trim();
    if (!trimmed) return toast.error("Type your name first.");
    const font = SIG_FONTS.find((f) => f.name === fontName) || SIG_FONTS[0];
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
    // Autofit — shrink font until the name fits within 92% of canvas width.
    let px = 72;
    ctx.fillStyle = "#0f172a";
    ctx.textBaseline = "middle";
    while (px > 24) {
      ctx.font = `${px}px ${font.family}`;
      if (ctx.measureText(trimmed).width <= c.width * 0.92) break;
      px -= 2;
    }
    ctx.textAlign = "center";
    ctx.fillText(trimmed, c.width / 2, c.height / 2);
    // Flush to witness stream so the counterparty sees the signature appear.
    strokeBuffer.current = [];
    flushStrokes(false);
    toast.success("Signature adopted — click Sign & submit when ready.");
  }

  /**
   * handleSigUpload — draw an uploaded PNG/JPG signature into the canvas
   * (auto-cropped to fit). Same canvas → same submit contract, so the
   * server receives the exact same audited artefact as a drawn signature.
   */
  function handleSigUpload(file) {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) return toast.error("Signature image must be under 2 MB.");
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const c = canvasRef.current;
        if (!c) return;
        const ctx = c.getContext("2d");
        ctx.clearRect(0, 0, c.width, c.height);
        const scale = Math.min(c.width / img.width, c.height / img.height, 1);
        const w = img.width * scale;
        const h = img.height * scale;
        ctx.drawImage(img, (c.width - w) / 2, (c.height - h) / 2, w, h);
        strokeBuffer.current = [];
        flushStrokes(false);
        toast.success("Signature loaded — click Sign & submit when ready.");
      };
      img.onerror = () => toast.error("Could not read that image — please try a different file.");
      img.src = String(ev.target?.result || "");
    };
    reader.readAsDataURL(file);
  }

  async function submitSig() {
    if (!typedName.trim()) return toast.error("Type your name to confirm intent to sign.");
    const dataUrl = canvasRef.current.toDataURL("image/png");
    // Reject a completely-blank pad
    if (dataUrl.length < 2500) return toast.error("Please draw your signature.");
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/esign/envelopes/${eid}/sign`, {
        method: "POST",
        headers: { ...auth(), "Content-Type": "application/json" },
        body: JSON.stringify({ signature_png_b64: dataUrl, typed_name: typedName.trim() }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Signing failed");
      setEnv(d);
      toast.success("Signature captured");
    } catch (err) { toast.error(String(err.message)); }
    finally { setSaving(false); }
  }

  // Poll live-strokes every 700ms so we can render the other party's
  // pen movement live on their witness canvas.
  useEffect(() => {
    if (!env || !meSigner) return;
    let cancelled = false;
    const timer = setInterval(async () => {
      if (env?.signers?.every((s) => !!s.signed_at)) return;
      try {
        const r = await fetch(`${API}/api/esign/envelopes/${eid}/live-stroke`, { headers: auth() });
        if (!r.ok) return;
        const d = await r.json();
        if (cancelled) return;
        Object.entries(d.strokes || {}).forEach(([role, row]) => {
          if (role === meSigner.role) return;   // don't overwrite my own pad
          const c = witnessCanvasRefs.current[role];
          if (!c) return;
          const ctx = c.getContext("2d");
          ctx.clearRect(0, 0, c.width, c.height);
          const pts = row.points || [];
          if (pts.length < 2) return;
          ctx.lineWidth = 2.2;
          ctx.lineCap = "round";
          ctx.strokeStyle = "#0f172a";
          ctx.beginPath();
          ctx.moveTo(pts[0][0], pts[0][1]);
          for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i][0], pts[i][1]);
          ctx.stroke();
        });
      } catch { /* silent */ }
    }, 700);
    return () => { cancelled = true; clearInterval(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eid, env?.id, meSigner?.role]);

  if (loading) return <Loader/>;
  if (!env) return (
    <div className="p-8 text-center text-slate-500">Envelope not found. <Link to="/leases" className="text-emerald-700 font-bold underline">Back to leases</Link></div>
  );

  return (
    <div data-testid="lease-sign-page">
      <PageHeader title={env.title || "Lease agreement"} subtitle={`Envelope ${env.id}`}/>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        {env.signers.map((s) => (
          <Pill key={s.role} tone={s.signed_at ? "green" : "amber"}>
            {s.signed_at ? <CheckCircle2 className="w-3 h-3 inline mr-1"/> : null}
            {s.role}: {s.signed_at ? "signed" : "pending"}
          </Pill>
        ))}
      </div>

      {/* Inline compliance check — surfaces RTA violations (illegal
          deposits, missing province, invalid postal codes) BEFORE the
          user signs, not after submit. */}
      <AutoCorrectness
        className="mb-4"
        schema_hint="lease"
        province={env?.province || env?.data?.province}
        payload={{
          title:       env?.title,
          province:    env?.province || env?.data?.province,
          rent:        env?.rent_cents ? Math.round(env.rent_cents / 100) : env?.data?.rent,
          deposit:     env?.deposit_cents ? Math.round(env.deposit_cents / 100) : env?.data?.deposit,
          start_date:  env?.start_date || env?.data?.start_date,
          end_date:    env?.end_date || env?.data?.end_date,
          postal:      env?.postal || env?.data?.postal,
          signers:     (env?.signers || []).map(s => ({ role: s.role, email: s.email })),
        }}
      />

      {/* Witness / real-time signer roster — always visible so both
          parties can see who has signed and when, and watch the pen. */}
      <Card className="p-4 mb-5" data-testid="signer-roster">
        <h3 className="font-display text-sm font-bold text-slate-900 mb-3 uppercase tracking-wider">Signing witness</h3>
        <div className="space-y-3">
          {env.signers.map((s) => {
            const isMe = s.email === (user?.email || "").toLowerCase();
            return (
              <div
                key={s.role}
                data-testid={`signer-row-${s.role}`}
                className={`rounded-xl border p-3 ${s.signed_at ? "bg-emerald-50 border-emerald-200" : "bg-amber-50/60 border-amber-200"}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold shrink-0 ${s.signed_at ? "bg-emerald-600" : "bg-amber-500"}`}>
                    {s.role[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-slate-900 capitalize inline-flex items-center gap-2">
                      {s.role}
                      {isMe && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-900 text-white">You</span>}
                    </div>
                    <div className="text-xs text-slate-600 truncate">{s.name || s.email}</div>
                    {s.signed_at ? (
                      <div className="text-[11px] text-emerald-800 mt-0.5">
                        <CheckCircle2 className="w-3 h-3 inline"/> Signed {new Date(s.signed_at).toLocaleString()}{s.ip ? ` · IP ${s.ip}` : ""}
                      </div>
                    ) : (
                      <div className="text-[11px] text-amber-800 mt-0.5">
                        <Clock className="w-3 h-3 inline"/> {isMe && !iSigned ? "It's your turn — draw your signature below." : "Waiting on this signer…"}
                      </div>
                    )}
                  </div>
                  {s.signed_at && s.sig_url && (
                    <img src={`${API}${s.sig_url}`} alt={`${s.role} signature`} className="h-10 max-w-[140px] object-contain bg-white rounded border border-slate-200 shrink-0" data-testid={`signer-thumb-${s.role}`}/>
                  )}
                </div>
                {/* Live pen — visible only for the OTHER party (not me),
                    only while they haven't signed yet. */}
                {!isMe && !s.signed_at && (
                  <div className="mt-2 rounded-lg border border-dashed border-slate-300 bg-white overflow-hidden" data-testid={`witness-canvas-wrap-${s.role}`}>
                    <div className="px-2 py-0.5 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Watching pen…</div>
                    <canvas
                      ref={(el) => { witnessCanvasRefs.current[s.role] = el; }}
                      width={640}
                      height={120}
                      className="w-full h-[110px]"
                      data-testid={`witness-canvas-${s.role}`}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Card>

      <Card className="p-5 mb-5" data-testid="lease-body">
        <h3 className="font-display text-base font-bold text-slate-900 mb-2 flex items-center gap-2"><FileText className="w-4 h-4"/> Agreement</h3>
        <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-slate-700">{env.body_text}</pre>
      </Card>

      {meSigner && !iSigned && (
        <Card className="p-5 mb-5" data-testid="signature-pad-card">
          <h3 className="font-display text-base font-bold text-slate-900 mb-3 flex items-center gap-2"><PenTool className="w-4 h-4"/> Your signature</h3>
          <Input label="Type your full legal name" value={typedName} onChange={(e) => setTypedName(e.target.value)} testid="typed-name-input"/>

          {/* Tabbed signature mode — Draw / Type / Upload, the pattern
              every modern e-sign tool (DocuSign, HelloSign, Dropbox
              Sign) uses. Each tab writes into the same canvas so the
              existing submit flow keeps working without a backend change. */}
          <div className="mt-4 inline-flex rounded-full bg-slate-100 p-1 text-xs font-bold" role="tablist" data-testid="signature-mode-tabs">
            <button type="button" role="tab" aria-selected={sigMode === "draw"} onClick={() => setSigMode("draw")} data-testid="signature-mode-draw"
              className={`px-3 py-1.5 rounded-full transition ${sigMode === "draw" ? "bg-white shadow text-slate-900" : "text-slate-600 hover:text-slate-900"}`}>
              Draw
            </button>
            <button type="button" role="tab" aria-selected={sigMode === "type"} onClick={() => setSigMode("type")} data-testid="signature-mode-type"
              className={`px-3 py-1.5 rounded-full transition ${sigMode === "type" ? "bg-white shadow text-slate-900" : "text-slate-600 hover:text-slate-900"}`}>
              Type
            </button>
            <button type="button" role="tab" aria-selected={sigMode === "upload"} onClick={() => setSigMode("upload")} data-testid="signature-mode-upload"
              className={`px-3 py-1.5 rounded-full transition ${sigMode === "upload" ? "bg-white shadow text-slate-900" : "text-slate-600 hover:text-slate-900"}`}>
              Upload
            </button>
          </div>

          <div className="mt-3 border-2 border-dashed border-slate-300 rounded-xl bg-white touch-none">
            <canvas
              ref={canvasRef}
              width={640}
              height={200}
              className={`w-full h-[180px] rounded-xl ${sigMode === "draw" ? "cursor-crosshair" : ""}`}
              data-testid="signature-canvas"
              onMouseDown={sigMode === "draw" ? startDraw : undefined}
              onMouseMove={sigMode === "draw" ? moveDraw : undefined}
              onMouseUp={sigMode === "draw" ? endDraw : undefined}
              onMouseLeave={sigMode === "draw" ? endDraw : undefined}
              onTouchStart={sigMode === "draw" ? startDraw : undefined}
              onTouchMove={sigMode === "draw" ? moveDraw : undefined}
              onTouchEnd={sigMode === "draw" ? endDraw : undefined}
            />
          </div>

          {/* Type-mode controls — cursive-font style picker, "Adopt" writes
              the styled name into the same canvas the submit flow reads. */}
          {sigMode === "type" && (
            <div className="mt-3 space-y-3" data-testid="signature-type-panel">
              <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500">Pick a signature style</div>
              <div className="grid sm:grid-cols-2 gap-2">
                {SIG_FONTS.map((f) => {
                  const isActive = typeFont === f.name;
                  return (
                    <button
                      type="button"
                      key={f.name}
                      onClick={() => setTypeFont(f.name)}
                      data-testid={`signature-font-${f.name.replace(/\s+/g, "-").toLowerCase()}`}
                      className={`text-left px-4 py-3 rounded-xl border transition ${isActive ? "border-emerald-500 bg-emerald-50 ring-2 ring-emerald-200" : "border-slate-200 hover:border-slate-300 bg-white"}`}
                    >
                      <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">{f.name}</div>
                      <div className="mt-0.5 text-2xl text-slate-900 leading-tight" style={{ fontFamily: f.family }}>
                        {(typedName || "Your Name").slice(0, 32)}
                      </div>
                    </button>
                  );
                })}
              </div>
              <Button
                type="button"
                onClick={() => renderTypedSignature(typedName, typeFont)}
                disabled={!typedName.trim()}
                data-testid="signature-adopt-typed"
                variant="secondary"
              >
                Adopt this signature
              </Button>
            </div>
          )}

          {/* Upload-mode controls — drop a PNG/JPG of a signature, we
              draw it into the canvas so the same submit path still works. */}
          {sigMode === "upload" && (
            <div className="mt-3" data-testid="signature-upload-panel">
              <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-300 bg-white hover:bg-slate-50 text-sm font-bold text-slate-800" data-testid="signature-upload-btn">
                <Upload className="w-4 h-4" /> Upload signature image
                <input type="file" accept="image/png,image/jpeg" className="hidden" onChange={(e) => handleSigUpload(e.target.files?.[0])} data-testid="signature-upload-input" />
              </label>
              <p className="mt-1.5 text-[11px] text-slate-500">PNG or JPG, transparent background preferred, up to 2 MB.</p>
            </div>
          )}

          <div className="mt-4 flex items-center justify-between">
            <button onClick={clearPad} className="text-xs font-bold text-slate-600 hover:text-slate-900 inline-flex items-center gap-1" data-testid="signature-clear">
              <RotateCcw className="w-3.5 h-3.5"/> Clear
            </button>
            <Button onClick={submitSig} disabled={saving} data-testid="signature-submit">
              {saving ? "Submitting…" : "Sign & submit"}
            </Button>
          </div>
        </Card>
      )}

      {iSigned && !bothSigned && (
        <Card className="p-4 mb-5 border-amber-200 bg-amber-50 text-sm text-amber-800" data-testid="pending-other-party">
          You&apos;ve signed. Waiting for the other party to sign this envelope.
        </Card>
      )}

      {bothSigned && (
        <Card className="p-4 border-emerald-200 bg-emerald-50 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="fully-signed">
          <div className="text-sm text-emerald-900 font-bold flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4"/> Fully signed. Download the audited PDF anytime.
          </div>
          <a
            href={`${API}/api/esign/envelopes/${eid}/pdf`}
            target="_blank" rel="noreferrer"
            data-testid="lease-pdf-download"
          >
            <Button className="!py-1.5 !px-3 text-xs"><Download className="w-3 h-3 inline mr-1"/>Download signed PDF</Button>
          </a>
        </Card>
      )}
    </div>
  );
}
